import React from "react";
import {Tooltip, Menu, MenuItem, IconButton, Checkbox} from "@mui/material";
import ListIcon from '@mui/icons-material/List';
import {GridRenderCellParams} from "@mui/x-data-grid";
import {CSSProperties, useState} from "react";
import NumberFormat from "react-number-format";
import digitsOnly from "../utils/digitsOnly";
import {t} from "i18next";
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';

export const columnStyle: CSSProperties = {
    whiteSpace: "pre-wrap",
    overflow: "hidden",
    textOverflow: "ellipsis"
};

export const largeColumnStyle: CSSProperties = {
    display: "flex",
    width: "100%",
    height: "100%",
    padding: "5px 0"
}

const innerStyle: CSSProperties = {
    display: "flex",
}

export function Column(
    field: string,
    width: number,
    style: CSSProperties,
    tDictionaryKey?: string,
    tHeaderKey: string = 'auction'
) {
    return {
        field, headerName: t(`${tHeaderKey}.table_columns.${field}`), width,
        renderCell: (params: GridRenderCellParams<any, any, any>) => (
            <Tooltip title={tDictionaryKey ? t(`${tDictionaryKey}.${params.row[field]}`) as string : params.row[field]}>
                <div style={style}>
                    <div style={innerStyle}>
                        {tDictionaryKey
                            ? t(`${tDictionaryKey}.${params.row[field]}`)
                            : params.row[field]}
                    </div>
                </div>
            </Tooltip>
        ),
    }
}

export function CompanyTitleColumn(
    width: number,
    style: CSSProperties
) {
    return {
        field: "companyTitle", headerName: t("auction.table_columns.company.title"), width,
        renderCell: (params: GridRenderCellParams<any, any, any>) => (
            <Tooltip title={params.row.company.title}>
                <div style={style}>
                    <div style={innerStyle}>{params.row.company.title}</div>
                </div>
            </Tooltip>
        ),
    }
}

export function CompanyItnColumn(
    width: number,
    style: CSSProperties
) {
    return {
        field: "companyItn", headerName: t("auction.table_columns.itn"), width,
        renderCell: (params: GridRenderCellParams<any, any, any>) => (
            <Tooltip title={digitsOnly(params.row.itn)}>
                <div style={style}>
                    <div style={innerStyle}>{digitsOnly(params.row.itn)}</div>
                </div>
            </Tooltip>
        ),
    }
}

export function GrossColumn(
    width: number,
    style: CSSProperties,
    tHeaderKey: string = 'auction'
) {
    return {
        field: "gross", headerName: t(`${tHeaderKey}.table_columns.gross`), width,
        renderCell: (params: GridRenderCellParams<any, any, any>) => (
            <Tooltip title={params.row?.gross}>
                <div style={style}>
                    <NumberFormat
                        value={params.row?.gross}
                        thousandsGroupStyle="thousand"
                        decimalSeparator="."
                        displayType="text"
                        type="text"
                        thousandSeparator={" "}
                        allowNegative={false}
                    />
                </div>
            </Tooltip>
        ),
    }
}

export function DateColumn(
    field: string,
    width: number,
    style: CSSProperties,
    tHeaderKey: string = 'auction'
) {
    return {
        field, headerName: t(`${tHeaderKey}.table_columns.${field}`), width,
        renderCell: (params: GridRenderCellParams<any, any, any>) => (
            (!!params.row[field]) ?
                <Tooltip title={params.row[field].replace('T', " ") ?? params.row[field]}>
                    <div style={style}>
                        <div style={innerStyle}>{params.row[field].replace('T', " ") ?? params.row[field]}</div>
                    </div>
                </Tooltip> : <div></div>
        ),
    }
}

export function DateTimeColumn(
    field: string,
    width: number,
    style: CSSProperties,
    tHeaderKey: string = 'auction'
) {
    return {
        field, headerName: t(`${tHeaderKey}.table_columns.${field}`), width,
        renderCell: (params: GridRenderCellParams<any, any, any>) => {
            // Проверяем наличие значения
            if (!params.row[field]) return <div></div>;

            // Проверяем, что значение можно преобразовать в дату
            let formattedDate = '';
            try {
                const dateObj = new Date(params.row[field]);

                // Проверяем, что получилась действительная дата
                if (!isNaN(dateObj.getTime())) {
                    formattedDate = `${dateObj.getDate().toString().padStart(2, '0')}.${(dateObj.getMonth() + 1).toString().padStart(2, '0')}.${dateObj.getFullYear()} ${dateObj.getHours().toString().padStart(2, '0')}:${dateObj.getMinutes().toString().padStart(2, '0')}`;
                } else {
                    // Если не удалось создать объект даты, используем исходную строку
                    formattedDate = typeof params.row[field] === 'string' ? params.row[field].replace('T', ' ') : String(params.row[field]);
                }
            } catch (error) {
                // В случае ошибки преобразования используем исходную строку
                formattedDate = typeof params.row[field] === 'string' ? params.row[field].replace('T', ' ') : String(params.row[field]);
            }

            return (
                <Tooltip title={formattedDate}>
                    <div style={style}>
                        <div style={innerStyle}>{formattedDate}</div>
                    </div>
                </Tooltip>
            );
        },
    }
}

export function OwnerNameColumn(
    width: number,
    style: CSSProperties
) {
    return {
        field: "ownerName", headerName: t("auction.table_columns.ownerName"), width,
        renderCell: (params: GridRenderCellParams<any, any, any>) => (
            <Tooltip title={params.row.ownerData.name}>
                <div style={style}>
                    <div style={innerStyle}>{params.row.ownerData.name}</div>
                </div>
            </Tooltip>
        ),
    }
}

export function AuthoritiesColumn(
    width: number,
    style: CSSProperties
) {
    return {
        field: "authorities", headerName: t("auction.table_columns.authorities"), width,
        renderCell: (params: GridRenderCellParams<any, any, any>) => (
            <Tooltip title={t(`authority.${params.row.authorities[params.row.authorities.length - 1]}`) as string}>
                <div style={style}>
                    <div style={innerStyle}>
                        {t(`authority.${params.row.authorities[params.row.authorities.length - 1]}`)}
                    </div>
                </div>
            </Tooltip>
        ),
    }
}

export interface ActionMenuProps {
    row: any;
    actions: { label: string; callback: (row: any) => void; predicate?: (row: any) => boolean }[];
    style: CSSProperties;
    tHeaderKey: string;
}

const ActionMenu: React.FC<ActionMenuProps> = ({row, actions, style, tHeaderKey}) => {
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

    const handleMenuOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    return (
        <div style={style}>
            <Tooltip title={t(`${tHeaderKey}.table_columns.action`)}>
                <IconButton size="small" onClick={handleMenuOpen}>
                    <ListIcon/>
                </IconButton>
            </Tooltip>

            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
                PaperProps={{
                    style: {
                        maxHeight: 200,
                        width: "20ch",
                    },
                }}
            >
                {actions
                    .filter(action => !action.predicate || action.predicate(row)) // Фильтрация по predicate
                    .map((action, index) => (
                        <MenuItem
                            key={index}
                            onClick={() => {
                                action.callback(row); // Вызываем callback
                                handleMenuClose(); // Закрываем меню
                            }}
                        >
                            {t(action.label)}
                        </MenuItem>
                    ))}
            </Menu>
        </div>
    );
};

export function ActionColumn(
    width: number,
    style: React.CSSProperties,
    tHeaderKey: string = 'auction',
    actions: { label: string; callback: (row: any) => void; predicate?: (row: any) => boolean }[]
) {
    return {
        field: "action",
        headerName: t(`${tHeaderKey}.table_columns.action`),
        width,
        renderCell: (params: GridRenderCellParams<any, any, any>) => (
            <ActionMenu row={params.row} actions={actions} style={style} tHeaderKey={tHeaderKey}/>
        ),
    };
}

export function CheckboxColumn<T>(
    field: string,
    width: number,
    tHeaderKey: string = 'auction',
    onChange: (value: T, checked: boolean) => void
) {
    return {
        field,
        headerName: t(`${tHeaderKey}.table_columns.checkbox`),
        width,
        sortable: false,
        filterable: false,
        renderCell: (params: GridRenderCellParams) => (
            <Checkbox
                checked={params.row[field]}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => onChange(params.row, event.target.checked)}
                color="primary"
            />
        ),
    };
}

export function ExpandColumn<ID, T>(
    field: string,
    width: number,
    tHeaderKey: string = 'auction',
    toggleRow: (id: ID) => void,
    expandedRows: boolean[]
) {
    return {
        field: field,
        headerName: '',
        width: width,
        renderCell: (params: GridRenderCellParams) => (
            <IconButton
                aria-label="expand row"
                size="small"
                onClick={() => toggleRow(params.row.id)}
            >
                {expandedRows[params.row.id] ? (
                    <KeyboardArrowUpIcon />
                ) : (
                    <KeyboardArrowDownIcon />
                )}
            </IconButton>
        ),
        sortable: false,
        filterable: false,
    }
}
